<template>
  <div class="h-full">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <loading-spinner diameter="64px"/>
    </div>

    <alert-badge
        v-else-if="error.title"
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <div class="flex bg-white mx-4">
      <div class="cart w-3/4 mt-10 px-6">
        <div class="header h-14 flex flex-1 justify-between gap-2">
          <div class="search-box h-full w-full">
            <el-autocomplete
                v-model="itemSearchTerm"
                :fetch-suggestions="searchItems"
                :placeholder="t('searchItems')"
                @select="addItem"
                class="w-full h-full"
                value-key="name"
                @keypress.enter.prevent="onSearchSelectPressedEnter"
                @keyup.enter.prevent="onSearchSelectKeyupEnter"
                :trigger-on-focus="false"
                highlight-first-item
                :prefix-icon="Search"
                ref="searchInput"
            >
              <template #default="{ item }">
                <div
                    :class="{ 'flex-row-reverse': $ctx.locale == 'ar' }"
                    class="flex justify-between"
                >
                  <div>{{ item.name }}</div>
                  <div v-if="!item.unlimitedStock">
                    {{ item.currentLocationStock.quantity }}
                  </div>
                </div>
              </template>
            </el-autocomplete>
          </div>
        </div>

        <div class="flex flex-row w-full overflow-x-auto pb-4"
             v-if="top8Items.length > 0">
          <div
              v-for="item in top8Items"
              :key="item.id"
              class="bg-gray-100 p-2 mx-2 text-nowrap rounded cursor-pointer hover:bg-gray-400 hover:text-white select-none"
              @click="addItem(item)"
          >
            {{ item.name }}
          </div>
        </div>

        <div class="items-table w-full mt-6 overflow-y-scroll flex flex-col">
          <el-table
              :table-layout="'fixed'"
              :data="inventoryReceiving.inventoryReceivingItems"
              stripe
              border
              style="width: 100%"
              height="100%"
              class="card rounded"
              header-cell-class-name="my-header"
          >
            <el-table-column :label="t('item')" prop="item.name"/>
            <el-table-column :label="t('itemCost')">
              <template #default="scope">
                <money-input id="cost" :currency="$ctx.currency"
                             v-model="scope.row.costAmount"/>
              </template>
            </el-table-column>
            <el-table-column :label="t('quantity')">
              <template #default="scope">
                <!--                <el-input type="number" v-model="scope.row.quantity" :min="1"></el-input>-->
                <QuantityInput :item="scope.row.item"
                               :max="scope.row.quantity"
                               v-model="scope.row.quantity"></QuantityInput>
              </template>
            </el-table-column>
            <el-table-column :label="t('totalCost')">
              <template #default="scope">
                <div>{{ calculateItemTotal(scope) }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="t('batchAndExpiryDate')" width="200">
              <template #default="scope">
                <div v-if="scope.row.item.isExpirable" class="flex flex-col">
                  <div>
                    <label><span class="text-red-500">*</span>{{ t('batchNumber') }}</label>
                    <el-input v-model="scope.row.extraProperties.batchNumber" style="width: 100%;" :placeholder="t('batchNumber')" />
                  </div>
                  <div>
                    <label>{{ t('manufactureDate') }}</label>
                    <el-date-picker v-model="scope.row.extraProperties.manufactureDate" style="width: 100%;" format="DD-MM-YYYY" :placeholder="t('manufactureDate')" :disabled-date="(date) => date > new Date()" />
                  </div>
                  <div>
                    <label><span class="text-red-500">*</span>{{ t('expiryDate') }}</label>
                    <el-date-picker v-model="scope.row.extraProperties.expiryDate" style="width: 100%;" format="DD-MM-YYYY" :placeholder="t('expiryDate')" :disabled-date="(date) => scope.row.extraProperties.manufactureDate ? date < new Date(scope.row.extraProperties.manufactureDate) : false" />
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="t('serialNumbers')"
                             prop="inventoryReceivingItemSerials">
              <template #default="scope">
                <tag-input
                    v-if="inventoryReceiving.inventoryReceivingItems[scope.$index].item.isSerialized"
                    v-model="scope.row.inventoryReceivingItemSerials"
                    :insertFunction="(val) => scope.row.insertSerialNumber(val)"
                    :removeFunction="(index) => scope.row.removeSerialNumber(index)"
                    :valueFormatter="(serialObject) => serialObject.itemSerial.number"
                    :validate="() => scope.row.validateSerials()"
                />
              </template>
            </el-table-column>
            <el-table-column :min-width="100" :label="t('notes')"
                             align="center">
              <template #default="scope">
                <el-input id="notes" v-model="scope.row.notes" show-word-limit
                          clearable>
                </el-input>
              </template>
            </el-table-column>
            <el-table-column align="center" width="55px">
              <template #default="scope">
                <horizontal-button
                    title=""
                    isDanger
                    faIcon="trash"
                    @click="removeItem(scope.$index)"
                    class="delete-btn"
                />
              </template>
            </el-table-column>
          </el-table>
          <div class="mb-2 mt-2">
            <div>{{ t('notes') }}</div>
            <div>
              <el-input v-model="inventoryReceiving.notes" :rows="2"
                        type="textarea"
                        placeholder=""/>
            </div>
          </div>
        </div>
      </div>

      <div class="aside w-1/4 border-l-2 flex flex-col mt-10 px-4">
        <div class="order-info my-4 bg-white">
          <div class="mt-2 cashier-info flex justify-between">
            <h1>{{ t('receivedBy') }}</h1>
            <h1>{{ ctx.currentUser.fullName }}</h1>
          </div>

          <el-divider/>

          <div class="p-4 bg-light-blue flex justify-between items-center">
            <div class="flex flex-col">
              <h1 class="text-l text-primary">
                {{ vendorName === '' ? t('noVendor') : vendorName }}
              </h1>
            </div>
            <font-awesome-icon
                v-if="vendorName"
                icon="times"
                class="text-primary cursor-pointer"
                @click="clearVendor"
            />
          </div>

          <div class="w-full self-end mt-1">
            <horizontal-button
                :title="t('buttons.addVendor')"
                faIcon="plus"
                styles="border border-primary text-primary button-secondary"
                @click="openVendorDialog"
                :rounded="false"
            />
          </div>

          <el-divider/>

          <div>
            <div>
              {{ t('otherCosts') }}
            </div>
            <div>
              <money-input
                  id="other-costs"
                  :currency="$ctx.currency"
                  v-model="inventoryReceiving.vendorBill.otherCostsAmount"
                  @dinero-created="
                (dinero) => (inventoryReceiving.vendorBill.otherCostsDinero = dinero)
              "
                  :key="inventoryReceiving.totalCostAmount"
                  :onchange="calculateInventoryTotal()"
              />
            </div>
          </div>

          <div class="items-total flex justify-between mt-4">
            <h1>{{ t('totalItems') }}</h1>
            <h1>{{ getTotalItemsCount() }}</h1>
          </div>


          <el-divider/>

          <div
              class="total flex flex-col justify-center items-center bg-yellow-100 p-2 -mx-1"
          >
            <h1 class="font-bold text-l">
              {{ t('total') }}
            </h1>
            <h1
                class="font-bold 2xl:text-3xl lg:text-2xl text-l mt-1"
            >
              {{
                formatMoney(inventoryReceiving.vendorBill.totalDueAmount, "IQD")
              }}
            </h1>
          </div>


          <div class="action-buttons">
            <horizontal-button
                :disabled="!inventoryReceiving.inventoryReceivingItems.length"
                :title="t('buttons.pay')"
                styles="button-primary 2xl:h-20 lg:h-16 h-12 2xl:text-3xl lg:text-2xl text-l font-bold mt-4"
                :rounded="false"
                @click="payDrawerOpen = true"
            />
          </div>
          <el-drawer
              v-model="payDrawerOpen"
              v-if="payDrawerOpen"
              :direction="'rtl'"
              @open="openPayDialog"
              :lock-scroll="false"
              class="overflow-scroll"
          >
            <div class="px-5" style="margin-top: -20px !important">
              <multi-currency-payment-input
                  v-model:total-paid="inventoryReceiving.vendorBill.totalPaidAmount"
                  v-model:change-due="inventoryReceiving.vendorBill.changeDue"
                  :totalDue="inventoryReceiving.vendorBill.totalDueAmount"
                  :iqdCashOnly="true"
                  :usePaidLabel="true"
              />

              <div class="mt-4">
                <div><span class="text-red-500 text-xl">*</span>{{ t('creditedAccount') }}</div>
                <div>
                  <el-select
                      id="credited-acount"
                      v-model="inventoryReceiving.payVendorBillFromAccountID"
                      :trigger-on-focus="false"
                      value-key="name"
                      class="w-full"
                      filterable
                  >
                    <el-option
                        :key="registerAccount.id"
                        :label="$ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName"
                        :value="registerAccount.id"
                    >
                      <div class="w-full bg-green-100">
                        {{ $ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName }}
                      </div>
                    </el-option>

                    <el-option
                        v-for="safe in safes"
                        :key="safe.id"
                        :label="$ctx.locale === 'ar' ? safe.arName : safe.enName"
                        :value="safe.id"
                    >
                      <div class="w-full bg-green-100">
                        {{ $ctx.locale === 'ar' ? safe.arName : safe.enName }}
                      </div>
                    </el-option>

                    <el-option
                        v-for="bank in banks"
                        :key="bank.id"
                        :label="$ctx.locale === 'ar' ? bank.arName : bank.enName"
                        :value="bank.id"
                    >
                      <div class="w-full bg-green-100">
                        {{ $ctx.locale === 'ar' ? bank.arName : bank.enName }}
                      </div>
                    </el-option>

                    <el-option
                        v-for="option in accounts"
                        :key="option.id"
                        :label="$ctx.locale === 'ar' ? option.arName : option.enName"
                        :value="option.id"
                    ></el-option>
                  </el-select>
                </div>
              </div>

              <div class="mt-4">
                <div>{{ t('notes') }}</div>
                <div>
                  <el-input v-model="inventoryReceiving.paymentNotes" :rows="2" type="textarea" placeholder="" />
                </div>
              </div>

              <div class="flex items-center justify-between my-2">
                <horizontal-button
                    class="text-lg complete-payment"
                    :title="t('buttons.completePay')"
                    @click="openCashierPinDialog()"
                    :rounded="false"
                />
              </div>
            </div>
          </el-drawer>
          <el-dialog
              :title="t('selectVendor')"
              v-model="vendorDialogOpen"
              width="30%"
              @close="closeVendorDialog"
          >
            <div class="flex items-center justify-center" :dir="$ctx.getDir()">
              <el-select
                  filterable
                  clearable
                  class="w-full"
                  v-model="inventoryReceiving.vendorID"
                  value-key="name"
                  remote
                  @change="onVendorNameChange"
                  :remote-method="searchVendors"
              >
                <el-option
                    v-for="option in vendors"
                    :key="option.id"
                    :label="option.name"
                    :value="option.id"
                ></el-option>
              </el-select>
            </div>
            <template #footer>
              <span class="dialog-footer">
                <el-button
                    @click="() => {
                      newVendorDialogVisible = true
                    }"
                >
                  <div class="flex items-center gap-2">
                    <span class="fa fa-plus"></span>
                    <span>{{ t('newVendor') }}</span>
                  </div>
                </el-button>
                <el-button type="primary" @click="closeVendorDialog">{{
                    t('buttons.confirm')
                  }}</el-button>
              </span>
            </template>
          </el-dialog>
          <NewVendorDialog v-model="newVendorDialogVisible" @onOpen="() => {vendorDialogOpen = false}" @onSubmit="onNewVendor"/>
          <el-dialog v-model="cashierPinDialogOpen" width="30%">
            <div>
              <!-- Enter the pin -->
              <div class="my-2">
                <p>{{ t('enterCashierPin') }}</p>
              </div>

              <!-- password input up to 6 chars -->
              <div class="my-2">
                <el-input
                    id="password"
                    v-model="cashierPin"
                    type="password"
                    required
                    show-password
                    autofocus
                    dir="ltr"
                    :maxlength="4"
                    :minlength="4"
                    @keyup.enter="validateSubmit('inventoryReceivingForm')"
                    ref="cashierPinInput"
                />
              </div>

              <div class="flex items-center justify-between mb-2">
                <horizontal-button
                    class="text-lg complete-payment"
                    :title="t('buttons.confirm')"
                    @click="validateSubmit('inventoryReceivingForm')"
                    :rounded="false"
                />
              </div>
            </div>
          </el-dialog>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {nextTick, onMounted, ref} from 'vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import Item from '@/models/inventory/Item'
import {i18nOrderMessages} from '@/models/sales/Order'
import {PurchaseOrder as Order} from '@/models/inventory/PurchaseOrder'
import {useHTTP} from '@/plugins/http'
import {useAlertModal} from '@/plugins/alert-modal/alert-modal'
import {useI18n} from 'vue-i18n'
import {Register} from '@/models/company/Register'
import {useContext} from '@/plugins/context'
import {useRoute, useRouter} from 'vue-router'
import AlertBadge from '@/components/AlertBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import {Route} from '@/router/routes'
import User from '@/models/user/User'
import Location from '@/models/company/Location'
import {ElForm, ElInput, ElMessage} from 'element-plus'
import {Search} from '@element-plus/icons-vue'
import QuantityInput from "@/views/inventory/items/QuantityInput.vue";
import ItemPack from "@/models/inventory/ItemPack";
import {useSettingsStore} from "@/stores/settingsStore";
import successbeep from "@/assets/sounds/success.mp3"
import errorbeep from "@/assets/sounds/error.mp3"
import Vendor from "@/models/vendor/Vendor";
import InventoryReceiving, {
  inventoryReceivingI18nMessages,
  InventoryReceivingItem
} from '@/models/inventory/InventoryReceiving'
import MoneyInput from "@/components/form/MoneyInput.vue";
import TagInput from "@/components/form/TagInput.vue";
import Dinero, {Currency} from "dinero.js";
import {formatDinero, formatMoney} from "@/utils/money";
import {Account} from "@/models/accounting/Account";
import MultiCurrencyPaymentInput
  from "@/components/money/MultiCurrencyPaymentInput.vue";
import NewVendorDialog from "@/views/companies/vendors/NewVendorDialog.vue";

const http = useHTTP()
const alertModal = useAlertModal()
const ctx = useContext()
const router = useRouter()
const route = useRoute()
const selectedIndex = ref()
const itemSearchTerm = ref('')
const currentItemPack = ref<ItemPack | null>()
const serialSearchTerm = ref('')
const order = ref(new Order())
const inventoryReceiving = ref(new InventoryReceiving())
const accounts = ref<Account[]>([])
const registerAccount = ref<Account>(new Account())
const safes = ref<Account[]>([])
const banks = ref<Account[]>([])
const payDrawerOpen = ref(false)


const pressedEnter = ref(false)
const searchLoading = ref(false)
const error = ref({title: '', body: ''})
const loading = ref(false)
const vendorDialogOpen = ref(false)
const newVendorDialogVisible = ref(false)
const vendors = ref(new Array<Vendor>())
const vendor = ref(null as Vendor | null)
const vendorName = ref('')
const direction = ref('rtl')

const cashierPin = ref('')
const cashierPinInput = ref()
const cashierPinDialogOpen = ref(false)

const searchInput = ref()
const top8Items = ref([] as Array<Item>)
const tempSearchResult = ref<Array<Item>>(null)

const settingsStore = useSettingsStore();
const {t} = useI18n({
  useScope: 'global',
  messages: {
    en: {
      ...i18nOrderMessages.en,
      ...inventoryReceivingI18nMessages.en,
      newVendor: 'New Vendor',
      notes: 'Notes',
      totalItems: 'Total Items',
      searchItems: 'Enter item barcode or SKU or name',
      delete: 'Delete',
      noVendor: 'No Vendor Selected',
      enterCashierPin: 'Enter cashier pin',
      buttons: {
        addVendor: 'Add Vendor',
        pay: 'Pay',
        completePay: 'Process Payment',
        confirm: "Confirm"
      },

      selectSerials: 'Select serials',
      selectVendor: 'Select a Vendor',
      successMsg: 'Sale complete',

      errCashierPinRequired: 'Cashier pin is required',
      itemNotFound: 'Item Not Found',
      receivedBy: "Received By",
      creditedAccount: 'Pay the vendor from account:',
      creditedAccountRequired: 'Please select vendor payment account',
      batchAndExpiryDate: 'Batch and Expiry Date',
      batchNumber: 'Batch Number',
      manufactureDate: 'Manufacture Date',
      expiryDate: 'Expiry Date',
      errManufactureDateFuture: 'Manufacture date cannot be in the future',
      errExpiryBeforeManufacture: 'Expiry date cannot be before manufacture date',

    },
    ar: {
      ...i18nOrderMessages.ar,
      ...inventoryReceivingI18nMessages.ar,
      newVendor: 'مورّد جديد',
      notes: 'الملاحظات',
      totalItems: 'إجمالي العناصر',
      searchItems: 'ادخل الباركود او ال SKU او اسم المنتج',
      delete: 'حذف',
      noVendor: 'لم يتم اختيار أي مورّد',
      enterCashierPin: 'ادخل رمز الكاشير الخاص بك',
      buttons: {
        addVendor: 'اضافة مورّد',
        pay: 'دفع',
        completePay: 'اتمام الدفع',
        confirm: "تأكيد"
      },

      selectSerials: 'اختر الارقام',
      selectVendor: 'اختر مورّد',
      successMsg: 'تم اكمال البيع',
      errCashierPinRequired: 'يرجى ادخال رمز الكاشير لاتمام البيع',
      itemNotFound: 'لم يتم ايجاد المنتج',
      receivedBy: "المستلم",
      creditedAccount: 'ادفع للمورّد من الحساب:',
      creditedAccountRequired: 'يرجى اختيار حساب الدفع للمورّد',
      batchAndExpiryDate: 'رقم الوجبة وتاريخ الإنتهاء',
      batchNumber: 'رقم الوجبة',
      expiryDate: 'تاريخ الإنتهاء',
      manufactureDate: 'تاريخ الصنع',
      errManufactureDateFuture: 'تاريخ الصنع لا يمكن أن يكون في المستقبل',
      errExpiryBeforeManufacture: 'تاريخ الإنتهاء لا يمكن أن يكون قبل تاريخ الصنع'

    }
  }
})

onMounted(async () => {
  try {
    loading.value = true

    const top8ItemsResp = await http.get<Item[]>('/inventory/items/top8-purchased-items')
    top8Items.value = top8ItemsResp.map((item) => Item.from(item))

    registerAccount.value = Account.from(
        await http.get<Account>(`${Account.ENDPOINT}/${ctx.currentRegister.accountID}`)
    )
    const safesResp = await http.get<Account[]>('accounting/accounts/safes?skip_balance=true')
    safes.value = safesResp?.map((s) => Account.from(s))
    const banksResp = await http.get<Account[]>('accounting/accounts/banks?skip_balance=true')
    banks.value = banksResp?.map((b) => Account.from(b))

    const poRes = await http.get<Order>(`${Order.ENDPOINT}/${route.params.id}`);
    order.value = Order.from(poRes);
    loadPurchaseOrder();

  } catch (error) {
    console.log(error)
    error.value.title = error.title
    error.value.body = error.body
  } finally {
    loading.value = false
    setTimeout(() => {
      searchInput.value.focus()
    }, 100)
  }
})

function loadPurchaseOrder() {
  vendors.value = [order.value.vendor]
  selectedIndex.value = 0;
  inventoryReceiving.value.vendorID = order.value.vendorID
  inventoryReceiving.value.vendor = order.value.vendor
  inventoryReceiving.value.notes = order.value.notes
  vendorName.value = order.value.vendor?.name

  for (const oi of order.value.orderItems) {
    if (oi.receivedQuantity != oi.quantity) {
      const ivi = new InventoryReceivingItem(oi.item);
      ivi.quantity = oi.quantity - oi.receivedQuantity;
      ivi.itemID = oi.itemID
      ivi.costAmount = oi.item.costAmount || 0
      ivi.notes = oi.notes
      ivi.purchaseOrderItemID = oi.id
      inventoryReceiving.value.inventoryReceivingItems.push(ivi)
    }
  }
}


function addItem(selected: Item) {
  const tmpItemPack = currentItemPack.value;
  currentItemPack.value = null;

  if (selected.unlimitedStock) {
    itemSearchTerm.value = ''
    error.value.title = t('errUnlimitedItemsCanotBeReceived');
    return;
  }

  if (tmpItemPack) {
    if (tmpItemPack.disabled) {
      return;
    }
    inventoryReceiving.value.addItemPack(selected, tmpItemPack)
  } else {
    inventoryReceiving.value.addItem(selected)
  }
  calculateInventoryTotal()
  itemSearchTerm.value = ''
}

async function searchItems(query: string, cb: Function) {
  searchLoading.value = true;
  serialSearchTerm.value = query
  if (pressedEnter.value) {
    itemSearchTerm.value = ''
  }

  try {
    const url = `${Item.ENDPOINT}?type=checkout&q=${query}`
    let items = await http.get<Item[]>(url)
    items = items.map((item) => Item.from(item))
    let temp = items.slice()

    // hide parent item and replace it with its variants
    for (let i = 0; i < items.length; i++) {
      if (items[i].hasVariants) {
        temp = temp.splice(i + 1, 1)
        // temp = [...temp.slice(0, i), ...temp.slice(i + 1)];

        temp.push(...items[i].variants.filter((i) => !i.disabled))
      }
    }

    items = temp
    tempSearchResult.value = temp

    // we need to check if this barcode or sku are packs related so we can select them
    let itemPack = null;
    if (items.length && Array.isArray(items[0].itemPacks)) {
      for (const ip of items[0].itemPacks) {
        if (ip.barcode == query || ip.sku == query) {
          itemPack = ip;
        }
      }
    }

    if (itemPack) {
      currentItemPack.value = itemPack;
    } else {
      currentItemPack.value = null;
    }

    // when entering a barcode, the scanner hits enter by default
    // so if we get back one result, we wanna append that right away
    // it makes the process much faster than make the user select by themselves
    if (pressedEnter.value && items.length === 1) {
      if (settingsStore.getValue('scan_beep') == 'true') {
        const errorAudio = new Audio(successbeep);
        errorAudio.play();
      }
      addItem(items[0])
      cb([])
      tempSearchResult.value = null;
    } else if (pressedEnter.value && items.length == 0) {
      ElMessage.error({
        dangerouslyUseHTMLString: true,
        message: `<h2>${t('itemNotFound')}</h2>`
      })
      if (settingsStore.getValue('scan_error_beep') == 'true') {
        const errorAudio = new Audio(errorbeep);
        errorAudio.play();
      }
      cb([])
      tempSearchResult.value = null;
    } else {
      // show them all options
      cb(items)

    }
  } catch (error) {
    alertModal.showDanger({title: error.title})
    console.log(error)

    cb([])
  }
  pressedEnter.value = false
  searchLoading.value = false;
}


function openVendorDialog() {
  ctx.getDir() === 'rtl' ? (direction.value = 'rtl') : (direction.value = 'ltr')

  vendorDialogOpen.value = true
}

function closeVendorDialog() {
  if (selectedIndex.value == undefined) {
    vendorName.value = ''
    vendor.value = null
    inventoryReceiving.value.vendorID = undefined
  } else if (selectedIndex.value >= 0) {
    vendorName.value = vendors.value[selectedIndex.value].name
    vendor.value = vendors.value[selectedIndex.value]
    inventoryReceiving.value.vendorID = vendors.value[selectedIndex.value].id
  } else {
    vendorName.value = ''
    vendor.value = null
    inventoryReceiving.value.vendorID = undefined
  }

  vendorDialogOpen.value = false
}

function onNewVendor(newVendor: Vendor) {
  vendorName.value = newVendor.name
  vendor.value = newVendor
  order.value.vendorID = newVendor.id
  newVendorDialogVisible.value = false
  vendors.value = [newVendor]
  selectedIndex.value = 0
  vendorDialogOpen.value = false;
}

function onVendorNameChange(value: string) {
  selectedIndex.value = vendors.value.findIndex((vendor) => vendor.id === value)
}

function clearVendor() {
  vendorName.value = ''
  inventoryReceiving.value.vendorID = ''
  selectedIndex.value = null
  vendors.value = null
}

async function searchVendors(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Vendor.ENDPOINT}?q=${query}`
    const vendorsRes = await http.get<Vendor[]>(url)
    vendors.value = vendorsRes.map((vendor) => Vendor.from(vendor))
  } catch (error) {
    alertModal.showDanger({title: error.title})
    vendors.value = []
  }
}

function getTotalItemsCount() {
  const totalCount = inventoryReceiving.value.inventoryReceivingItems.reduce((acc, item) => {
    return acc + Number(item.quantity)
  }, 0)

  return totalCount
}

async function openCashierPinDialog() {
  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    cashierPinDialogOpen.value = true

    nextTick(() => {
      setTimeout(() => {
        cashierPinInput.value.focus()
      }, 0)
    })
    return;
  }
  validateSubmit('inventoryReceivingForm')
}

async function submit() {
  const ir = inventoryReceiving.value
  error.value.title = ''
  error.value.body = ''
  const data = {...ir};

  data.purchaseOrderID = order.value? order.value.id: undefined;
  data.locationID = ctx.currentLocation.id
  data.userID = ctx.currentUser.id
  data.vendorBill.currency = ctx.currency
  data.vendorBill.precision = ctx.currencyPrecision
  data.vendorID = ir.vendorID
  data.vendorBill.vendorID = ir.vendorID
  data.inventoryReceivingItems = ir.inventoryReceivingItems.map((inventoryReceivingItem) => {
    if (typeof inventoryReceivingItem.quantity === 'string')
      return {
        ...inventoryReceivingItem,
        quantity: parseInt(inventoryReceivingItem.quantity)
      } as InventoryReceivingItem
    return inventoryReceivingItem
  })
  try {
    await http.post(InventoryReceiving.ENDPOINT, data, {
      headers: { pin: cashierPin.value }
    })
    await router.push(Route.INVENTORY_PURCHASE_ORDERS)
  } catch (error) {
    console.log(error)
    return error
  }
}

function checkForm() {
  const errors = []
  error.value.body = ''
  for (const iri of inventoryReceiving.value.inventoryReceivingItems) {
    errors.push(iri.validateSerials())
    errors.push(iri.validateQuantity())

    // Validate batch dates for expirable items
    if (iri.item.isExpirable) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      // Check manufacture date is not in future
      if (iri.extraProperties.manufactureDate) {
        const mfgDate = new Date(iri.extraProperties.manufactureDate)
        mfgDate.setHours(0, 0, 0, 0)
        if (mfgDate > today) {
          errors.push(`${iri.item.name}: ${t('errManufactureDateFuture')}`)
        }
      }

      // Check expiry date is not before manufacture date
      if (iri.extraProperties.manufactureDate && iri.extraProperties.expiryDate) {
        const mfgDate = new Date(iri.extraProperties.manufactureDate)
        const expDate = new Date(iri.extraProperties.expiryDate)
        mfgDate.setHours(0, 0, 0, 0)
        expDate.setHours(0, 0, 0, 0)
        if (expDate < mfgDate) {
          errors.push(`${iri.item.name}: ${t('errExpiryBeforeManufacture')}`)
        }
      }
    }
  }

  error.value.body = errors.filter((err) => err).join('\n')

  if (error.value.body !== '') {
    error.value.title = t('validation.inputErrors')
    document.getElementById('main')?.scrollTo({ top: 0 })
    return false
  }

  if (!inventoryReceiving.value.payVendorBillFromAccountID && inventoryReceiving.value.vendorBill.totalPaidAmount > 0) {
    error.value.title = t('creditedAccountRequired')
    document.getElementById('main')?.scrollTo({ top: 0 })
    return false
  }

  return true
}


async function validateSubmit(formName: string) {
  error.value.title = '';

  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    if (cashierPin.value === '') {
      cashierPinDialogOpen.value = false
      error.value.title = t('errCashierPinRequired')
      return
    }
  }

  if (checkForm()) {
    // const form = this.$refs[formName] as typeof ElForm
    // form?.validate(async (valid: boolean) => {
    //   if (!valid) {
    //     document.getElementById('main')?.scrollTo({ top: 0 })
    //     cashierPinDialogOpen.value = false
    //     return false
    //   }
    //
    // })
    const resp = await submit()

    if (resp) {
      cashierPinDialogOpen.value = false
      error.value.title = resp?.title
      error.value.body = resp?.body
      document.getElementById('main')?.scrollTo({ top: 0 })
    } else {
      alertModal.showSuccess({ title: 'تم استلام البضاعة بنجاح' })
    }
  } else {
    cashierPinDialogOpen.value = false
    payDrawerOpen.value = false;
  }
}

function onSearchSelectPressedEnter() {
  pressedEnter.value = true;
  setTimeout(() => {
    if (tempSearchResult.value?.length == 0) {
      ElMessage.error({
        dangerouslyUseHTMLString: true,
        message: `<h2>${t('itemNotFound')}</h2>`
      })
      if (settingsStore.getValue('scan_error_beep') == 'true') {
        const errorAudio = new Audio(errorbeep);
        errorAudio.play();
      }
    }
  }, 500)
}

function onSearchSelectKeyupEnter() {
  setTimeout(() => {
    if (!searchLoading.value) {
      pressedEnter.value = false;
    }
  }, 400)
}

function calculateItemTotal(scope: any) {
  const price = Dinero({
    amount: scope.row.quantity * scope.row.costAmount,
    currency: ctx.currency,
    precision: ctx.currencyPrecision
  })
  return formatDinero(price)
}

function removeItem(index: number) {
  inventoryReceiving.value.removeItem(index)
  calculateInventoryTotal()
}

function calculateInventoryTotal() {
  let total = 0
  inventoryReceiving.value.inventoryReceivingItems.forEach((item) => {
    total += item.quantity * (item.costAmount ? item.costAmount : 0)
  })

  inventoryReceiving.value.totalCostAmount = total
  inventoryReceiving.value.totalCostDinero = Dinero({
    amount: total,
    currency: inventoryReceiving.value.vendorBill.currency as Currency,
    precision: 0
  })
  calculateTotal()
  return total
}

function
calculateTotal() {
  inventoryReceiving.value.vendorBill.totalDueAmount =
      (inventoryReceiving.value.totalCostAmount ? inventoryReceiving.value.totalCostAmount : 0) +
      (inventoryReceiving.value.vendorBill.otherCostsAmount
          ? inventoryReceiving.value.vendorBill.otherCostsAmount
          : 0)

  inventoryReceiving.value.vendorBill.totalDueDinero = Dinero({
    amount: inventoryReceiving.value.vendorBill.totalDueAmount,
    currency: inventoryReceiving.value.vendorBill.currency as Currency,
    precision: 0
  })
}

function openPayDialog() {
  nextTick(() => {
    /* @ts-ignore */
    // if (paymentInput.value) {
    //   /* @ts-ignore */
    //   paymentInput.cashReceivedInputFocus()
    // }
  })
}

</script>

<style>
@import url(../../../assets/css/index.css);

.bg-primary {
  background-color: var(--primary-bg-color);
}

.border-primary {
  border: 1px solid var(--primary-bg-color);
}

.text-primary {
  color: var(--primary-bg-color);
}

.bg-light-blue {
  background-color: #f2f7ff;
}

.item-name {
  width: 50%;
}

.item-price {
  height: inherit;
  width: auto;
}

.item-quantity {
  width: 15%;
}

.item-total {
  width: 15%;
}

.item-delete {
  width: 5%;
}

.el-radio-button__inner {
  width: 100% !important;
  font-size: 1.2rem;
}

.items-table {
  height: 65vh !important;
}

.el-drawer__header {
  padding-top: 5px;
}
</style>
